
import React, { useState, useEffect, useCallback, useMemo } from "react";
import api from "../api";
import PageLayout from "../components/PageLayout";
import { useToast } from '../context/ToastContext';
import { formatDateOnly } from '../utils/formatDate';
import { useClientPagination } from '../hooks/useClientPagination';
import TablePagination from '../components/TablePagination';
import TableSearch from '../components/TableSearch';

const ManagementCoupons = ({ embedded = false }) => {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingCoupon, setEditingCoupon] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const { success: toastSuccess, error: toastError } = useToast();

    const [formData, setFormData] = useState({
        code: "",
        description: "",
        discountType: "percent",
        discountValue: "",
        maxDiscount: "",
        minPurchase: "",
        validUntil: "",
        maxUsage: "",
        isActive: true
    });

    const filteredCoupons = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return coupons;
        return coupons.filter((c) =>
            [c.code, c.description, c.discount_type, c.is_active ? 'active' : 'inactive']
                .join(' ')
                .toLowerCase()
                .includes(q)
        );
    }, [coupons, searchTerm]);

    const page = useClientPagination(filteredCoupons, searchTerm);

    const fetchCoupons = useCallback(async () => {
        try {
            const res = await api.get("/api/settings/coupons");
            if (res.data.success) {
                setCoupons(res.data.coupons);
            }
        } catch (err) {
            toastError("Failed to fetch coupons");
        } finally {
            setLoading(false);
        }
    }, [toastError]);

    useEffect(() => {
        fetchCoupons();
    }, [fetchCoupons]);

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this coupon?")) return;
        try {
            const res = await api.delete(`/api/settings/coupons/${id}`);
            if (res.data.success) {
                toastSuccess("Coupon deleted");
                fetchCoupons();
            }
        } catch (err) {
            toastError("Failed to delete coupon");
        }
    };

    const handleEdit = (coupon) => {
        setEditingCoupon(coupon);
        setFormData({
            code: coupon.code,
            description: coupon.description || "",
            discountType: coupon.discount_type,
            discountValue: coupon.discount_value,
            maxDiscount: coupon.max_discount_amount || "",
            minPurchase: coupon.min_purchase_amount || "",
            validUntil: coupon.valid_until ? new Date(coupon.valid_until).toISOString().split('T')[0] : "",
            maxUsage: coupon.max_usage || "",
            isActive: coupon.is_active
        });
        setShowModal(true);
    };

    const handleCreate = () => {
        setEditingCoupon(null);
        setFormData({
            code: "",
            description: "",
            discountType: "percent",
            discountValue: "",
            maxDiscount: "",
            minPurchase: "",
            validUntil: "",
            maxUsage: "",
            isActive: true
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            let res;
            if (editingCoupon) {
                res = await api.put(`/api/settings/coupons/${editingCoupon.id}`, formData);
            } else {
                res = await api.post("/api/settings/coupons", formData);
            }

            if (res.data.success) {
                toastSuccess(editingCoupon ? "Coupon updated" : "Coupon created");
                setShowModal(false);
                fetchCoupons();
            }
        } catch (err) {
            toastError(err.response?.data?.message || `Failed to ${editingCoupon ? 'update' : 'create'} coupon`);
        }
    };

    const Content = () => (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div>
                    {!embedded && <h4 className="mb-0">Coupons</h4>}
                    {embedded && <h5 className="card-title mb-0">Coupons</h5>}
                    <p className="text-muted small mb-0">Manage discount codes and promotions</p>
                </div>
                <button className="btn btn-primary" onClick={handleCreate}>
                    <i className="las la-plus me-2"></i>New Coupon
                </button>
            </div>

            <div className="mb-3">
                <TableSearch
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="Search coupons by code, type, or status…"
                />
            </div>

            <div className="card shadow-sm border-0">
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="ps-4">Code</th>
                                    <th>Discount</th>
                                    <th>Validity</th>
                                    <th>Usage</th>
                                    <th>Status</th>
                                    <th className="text-end pe-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="6" className="text-center py-4">Loading...</td></tr>
                                ) : page.totalCount === 0 ? (
                                    <tr><td colSpan="6" className="text-center py-4 text-muted">No coupons found</td></tr>
                                ) : (
                                    page.items.map(coupon => (
                                        <tr key={coupon.id}>
                                            <td className="ps-4 fw-bold text-primary">{coupon.code}</td>
                                            <td>
                                                {coupon.discount_type === 'percent'
                                                    ? <span className="badge bg-info text-dark">{Number(coupon.discount_value)}% OFF</span>
                                                    : <span className="badge bg-success">${Number(coupon.discount_value)} OFF</span>
                                                }
                                                {coupon.max_discount_amount && <div className="small text-muted">Max ${coupon.max_discount_amount}</div>}
                                            </td>
                                            <td>
                                                {coupon.valid_until
                                                    ? formatDateOnly(coupon.valid_until)
                                                    : <span className="text-success">No Expiry</span>}
                                            </td>
                                            <td>
                                                {coupon.usage_count}
                                                {coupon.max_usage && <span className="text-muted"> / {coupon.max_usage}</span>}
                                            </td>
                                            <td>
                                                {coupon.is_active
                                                    ? <span className="badge bg-success bg-opacity-10 text-success">Active</span>
                                                    : <span className="badge bg-danger bg-opacity-10 text-danger">Inactive</span>}
                                            </td>
                                            <td className="text-end pe-4">
                                                <button
                                                    className="btn btn-sm btn-outline-primary me-2"
                                                    onClick={() => handleEdit(coupon)}
                                                    disabled={coupon.usage_count > 0}
                                                    title={coupon.usage_count > 0 ? "Cannot edit used coupons" : "Edit Coupon"}
                                                >
                                                    <i className="las la-edit"></i>
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleDelete(coupon.id)}
                                                >
                                                    <i className="las la-trash"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="px-3 pb-3">
                    <TablePagination
                        currentPage={page.currentPage}
                        totalPages={page.totalPages}
                        totalCount={page.totalCount}
                        showingFrom={page.showingFrom}
                        showingTo={page.showingTo}
                        onPageChange={page.setCurrentPage}
                    />
                </div>
            </div>

            {/* modal */}
            {showModal && (
                <div className="modal fade show block bg-[rgba(0,0,0,0.5)]">
                    <div className="modal-dialog modal-xl modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="row">
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Coupon Code</label>
                                            <input
                                                type="text"
                                                className="form-control text-uppercase"
                                                required
                                                placeholder="e.g. SUMMER2025"
                                                value={formData.code}
                                                onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase().replace(/\s/g, '') })}
                                            />
                                            <div className="form-text small">Alphanumeric, no spaces.</div>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Type</label>
                                            <select
                                                className="form-select"
                                                value={formData.discountType}
                                                onChange={e => setFormData({ ...formData, discountType: e.target.value })}
                                            >
                                                <option value="percent">Percentage (%)</option>
                                                <option value="flat">Flat Amount ($)</option>
                                            </select>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Value</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                required
                                                min="0"
                                                placeholder={formData.discountType === 'percent' ? "e.g. 20" : "e.g. 10"}
                                                value={formData.discountValue}
                                                onChange={e => setFormData({ ...formData, discountValue: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Valid Until</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={formData.validUntil}
                                                onChange={e => setFormData({ ...formData, validUntil: e.target.value })}
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Max Usage (Total)</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                placeholder="e.g. 1000"
                                                value={formData.maxUsage}
                                                onChange={e => setFormData({ ...formData, maxUsage: e.target.value })}
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            {formData.discountType === 'percent' && (
                                                <>
                                                    <label className="form-label">Max Discount ($)</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        placeholder="Cap amount"
                                                        value={formData.maxDiscount}
                                                        onChange={e => setFormData({ ...formData, maxDiscount: e.target.value })}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Description / Notes</label>
                                        <textarea
                                            className="form-control"
                                            rows="2"
                                            value={formData.description}
                                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                                        ></textarea>
                                    </div>

                                    <div className="mb-3 form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="isActiveSwitch"
                                            checked={formData.isActive}
                                            onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                                        />
                                        <label className="form-check-label" htmlFor="isActiveSwitch">
                                            Is Active
                                        </label>
                                    </div>

                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-primary">{editingCoupon ? 'Update Coupon' : 'Create Coupon'}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );

    if (embedded) {
        return <Content />;
    }

    return (
        <PageLayout title="Coupon Management">
            <Content />
        </PageLayout>
    );
};

export default ManagementCoupons;
